/**
 * Config merger for simulation server configs.
 *
 * Replicates the C# CLI's config loading logic:
 * 1. Load base config file by environment
 * 2. Load additional *.config.json files from config/ directory
 * 3. Deep merge with collision detection
 * 4. Validate structure and Firestore constraints
 */

import { existsSync, readFileSync, readdirSync, statSync } from 'fs'
import { join } from 'path'
import type { PlaygroundTarget } from '../config/playground'
import { resolveRundotConfigDir, rundotDisplayPrefix } from './rundotPaths'

export interface ConfigMergeResult {
  config: Record<string, unknown> | null
  /** Files that were loaded and merged */
  files: string[]
  warnings: string[]
  errors: string[]
}

/**
 * Map playground target to ordered config filename candidates.
 * Falls back to config.json when the target-specific file doesn't exist.
 */
function getBaseConfigCandidates(target: PlaygroundTarget): string[] {
  switch (target) {
    case 'local':
      return ['config.local.json', 'config.json']
    case 'dev':
    case 'staging':
      return ['config.staging.json', 'config.json']
    case 'playground':
    default:
      return ['config.json']
  }
}

export const SYSTEM_CONFIG_FILES = [
  'leaderboard.config.json',
  'rooms.config.json',
  'shop.config.json',
  'ugc.config.json',
  'storage.config.json',
  'inbox.config.json',
  'collectibles.config.json',
  'liveops.config.json',
  'textGen.config.json',
  'imageGen.config.json',
  'audioGen.config.json',
  'videoGen.config.json',
  'spriteGen.config.json',
  'threeDGen.config.json',
] as const

/**
 * True when `dir` holds at least one loadable server-config artifact: a recognized
 * `SYSTEM_CONFIG_FILES` entry or a `simulation/` subdirectory containing ≥1 `*.json`
 * file. Used to skip an empty canonical `rundot/` in favor of a populated legacy
 * `.rundot/`. The simulation check mirrors `loadRundotConfig`, which only loads
 * `simulation/*.json` — a bare `simulation/` dir loads nothing, so it must not count.
 */
function holdsServerConfig(dir: string): boolean {
  if (SYSTEM_CONFIG_FILES.some(f => existsSync(join(dir, f)))) return true
  // V2 room-type map (realtime.config.json) — counts ONLY when it actually
  // contributes deterministic rooms (derived into `rooms` below). A realtime file
  // with just non-deterministic (`file`) rooms loads nothing, so treating its mere
  // existence as config would let an otherwise-empty canonical `rundot/` shadow a
  // populated legacy `.rundot/` and silently drop it. On malformed JSON we DO count
  // it, so the dir resolves and `loadRundotConfig` surfaces the parse error loudly
  // rather than falling back silently.
  const realtimePath = join(dir, 'realtime.config.json')
  if (existsSync(realtimePath)) {
    try {
      if (readDeterministicRealtimeRooms(realtimePath, realtimePath).length > 0) return true
    } catch {
      return true
    }
  }
  const simDir = join(dir, 'simulation')
  try {
    if (!statSync(simDir).isDirectory()) return false
    return readdirSync(simDir).some(f => f.endsWith('.json'))
  } catch {
    return false
  }
}

/**
 * Load server config from the rundot folder (canonical `rundot/`, falling back to
 * legacy `.rundot/`). Returns null when neither folder is present or it holds no
 * server-config files.
 * Throws (loudly, naming the file) on double-wrap, simulation-wrapper, collision, or malformed JSON.
 */
function loadRundotConfig(projectRoot: string): { config: Record<string, unknown>; files: string[]; prefix: string } | null {
  // Pick the first candidate folder (canonical-preferred) that actually holds loadable server
  // config, so an empty `rundot/` never shadows a populated legacy `.rundot/`. Canonical still
  // wins when both hold config.
  const rundotDir = resolveRundotConfigDir(projectRoot, holdsServerConfig)
  if (!rundotDir) return null
  // `rundot/` or `.rundot/` — names the folder actually resolved in messages/file lists.
  const prefix = rundotDisplayPrefix(rundotDir)

  const merged: Record<string, unknown> = {}
  const files: string[] = []

  // Load per-system config files (filename → top-level key).
  for (const fileName of SYSTEM_CONFIG_FILES) {
    const filePath = join(rundotDir, fileName)
    if (!existsSync(filePath)) continue

    const parsed = parseJsonFile(filePath, `${prefix}${fileName}`)
    const key = fileName.replace('.config.json', '')

    // Reject double-wrapping: file should hold the value directly, not {"leaderboard": {...}}
    if (isPlainObject(parsed) && key in parsed) {
      throw new Error(
        `${prefix}${fileName} wraps its content in a top-level "${key}" key. ` +
        `It should contain the ${key} config value directly — the filename already implies it.`,
      )
    }

    merged[key] = parsed
    files.push(`${prefix}${fileName}`)
  }

  // Fallback: derive `rooms` from the deterministic rooms in realtime.config.json
  // (the V2 room-type map the sidecar + docs use) when no explicit rooms.config.json
  // was loaded. Keeps ONE canonical file working for the local sidecar AND hosted MP.
  if (!('rooms' in merged)) {
    const realtimePath = join(rundotDir, 'realtime.config.json')
    if (existsSync(realtimePath)) {
      const deterministic = readDeterministicRealtimeRooms(realtimePath, `${prefix}realtime.config.json`)
      if (deterministic.length > 0) {
        merged['rooms'] = deterministic
        files.push(`${prefix}realtime.config.json`)
      }
    }
  }

  // Load simulation directory (deep-merge all *.json files with collision detection).
  const simDir = join(rundotDir, 'simulation')
  if (existsSync(simDir)) {
    const simFiles = readdirSync(simDir)
      .filter(f => f.endsWith('.json'))
      .sort()

    const simMerged: Record<string, unknown> = {}
    for (const simFile of simFiles) {
      const parsed = parseJsonFile(join(simDir, simFile), `${prefix}simulation/${simFile}`)
      // Must be a JSON object — an array/scalar can't be deep-merged and would otherwise be
      // silently dropped.
      if (!isPlainObject(parsed)) {
        throw new Error(
          `${prefix}simulation/${simFile} must contain a JSON object at the top level (entities, recipes, etc.).`,
        )
      }
      // Reject files that still have the {"simulation": {...}} wrapper.
      if ('simulation' in parsed) {
        throw new Error(
          `${prefix}simulation/${simFile} contains a top-level "simulation" key. ` +
          `Files in ${prefix}simulation/ should contain raw content (entities, recipes, etc.) without the wrapper.`,
        )
      }
      // deepMerge already does collision detection (same rule as legacy config/)
      const errors = deepMerge(simMerged, parsed, `${prefix}simulation/${simFile}`)
      if (errors.length > 0) {
        throw new Error(errors.join('\n'))
      }
      files.push(`${prefix}simulation/${simFile}`)
    }

    if (Object.keys(simMerged).length > 0) {
      merged.simulation = simMerged
    }
  }

  if (files.length === 0) return null
  return { config: merged, files, prefix }
}

// Loud JSON parse that names the file (Req 13) — bare JSON.parse errors omit the path.
function parseJsonFile(absPath: string, displayName: string): unknown {
  const raw = readFileSync(absPath, 'utf-8')
  try {
    return JSON.parse(raw)
  } catch (err) {
    throw new Error(`${displayName} is not valid JSON: ${err instanceof Error ? err.message : String(err)}`)
  }
}

/**
 * Load ONLY `rundot/liveops.config.json` (canonical, falling back to legacy
 * `.rundot/`), returning its parsed `{ client?, server? }` value directly — or null
 * when absent. Deliberately isolated from `mergeGameConfigs`: mock mode consumes
 * just LiveOps, and a malformed sibling config (simulation, rooms, …) must not
 * suppress a perfectly valid liveops file. Throws (loudly, naming the file) on
 * malformed JSON or a double-wrapped `{"liveops": …}`, matching `loadRundotConfig`.
 */
export function loadRundotLiveOpsConfig(projectRoot: string): Record<string, unknown> | null {
  const rundotDir = resolveRundotConfigDir(projectRoot, holdsServerConfig)
  if (!rundotDir) return null

  const fileName = 'liveops.config.json'
  const filePath = join(rundotDir, fileName)
  if (!existsSync(filePath)) return null

  const prefix = rundotDisplayPrefix(rundotDir)
  const parsed = parseJsonFile(filePath, `${prefix}${fileName}`)
  if (!isPlainObject(parsed)) {
    throw new Error(`${prefix}${fileName} must contain a JSON object with "client"/"server" sections.`)
  }
  if ('liveops' in parsed) {
    throw new Error(
      `${prefix}${fileName} wraps its content in a top-level "liveops" key. ` +
      `It should contain the liveops config value directly — the filename already implies it.`,
    )
  }
  return parsed
}

/**
 * Merge game configs from the project directory.
 *
 * Loads the base config file and all *.config.json files from the config/ subdirectory,
 * then deep-merges them with collision detection (matching the C# CLI behavior).
 */
export function mergeGameConfigs(projectRoot: string, target: PlaygroundTarget): ConfigMergeResult {
  const result: ConfigMergeResult = {
    config: null,
    files: [],
    warnings: [],
    errors: [],
  }

  // Load .rundot/ into its own object — overlaid at the end so it always wins (never clobbered
  // by the legacy base load below). loadRundotConfig throws on malformed/double-wrapped/colliding
  // files; convert that into the result.errors contract callers already handle (no uncaught throw).
  let rundotConfig: { config: Record<string, unknown>; files: string[]; prefix: string } | null
  try {
    rundotConfig = loadRundotConfig(projectRoot)
  } catch (err) {
    result.errors.push(err instanceof Error ? err.message : String(err))
    return result
  }

  // Track which keys an env-specific base file defines, so we can warn when .rundot/ shadows them.
  const ENV_SPECIFIC = new Set(['config.local.json', 'config.staging.json'])
  let envBaseKeys = new Set<string>()

  // Step 1: Load base config file (first candidate that exists wins)
  const candidates = getBaseConfigCandidates(target)
  let baseFilename: string | null = null
  let merged: Record<string, unknown> = {}
  // Legacy-load failures are collected here (not pushed straight to result.errors) so a valid
  // .rundot/ config is never discarded by a broken legacy layout — resolved after the load below.
  const legacyErrors: string[] = []

  for (const candidate of candidates) {
    if (existsSync(join(projectRoot, candidate))) {
      baseFilename = candidate
      break
    }
  }

  if (baseFilename) {
    const basePath = join(projectRoot, baseFilename)
    try {
      const raw = readFileSync(basePath, 'utf-8')
      const parsed = JSON.parse(raw)
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        merged = parsed as Record<string, unknown>
        result.files.push(baseFilename)

        if (hasUnwrappedSimulationContent(merged)) {
          legacyErrors.push(
            `${baseFilename}: Simulation data (entities, recipes, lifecycle) must be wrapped in {"simulation": {...}}. ` +
            `Consider moving to rundot/simulation/ instead (run \`rundot migrate-config\`).`,
          )
        }

        if (ENV_SPECIFIC.has(baseFilename)) {
          envBaseKeys = new Set(Object.keys(merged))
        }
      } else {
        legacyErrors.push(`${baseFilename}: Expected a JSON object at root level`)
      }
    } catch (err) {
      legacyErrors.push(`${baseFilename}: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // Step 2: Scan config/ directory for *.config.json files. Loaded independently of the base so a
  // broken base doesn't discard valid overlays.
  const configDir = join(projectRoot, 'config')
  const additionalFiles: string[] = []

  if (existsSync(configDir)) {
    try {
      const entries = readdirSync(configDir)
      for (const entry of entries) {
        if (entry.endsWith('.config.json')) {
          additionalFiles.push(entry)
        }
      }
      additionalFiles.sort()
    } catch (err) {
      result.warnings.push(`Could not read config/ directory: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // Step 3: Load and merge each additional config file
  for (const filename of additionalFiles) {
    const filePath = join(configDir, filename)
    try {
      const raw = readFileSync(filePath, 'utf-8')
      const parsed = JSON.parse(raw)

      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        legacyErrors.push(`config/${filename}: Expected a JSON object at root level`)
        continue
      }

      const fileConfig = parsed as Record<string, unknown>

      // Validate structure: simulation configs must be wrapped in {"simulation": {...}}
      if (hasUnwrappedSimulationContent(fileConfig)) {
        legacyErrors.push(
          `config/${filename}: Simulation data (entities, recipes, lifecycle) must be wrapped in {"simulation": {...}}. ` +
          `Consider moving to .rundot/simulation/ instead (run \`rundot migrate-config\`).`,
        )
        continue
      }

      // Deep merge with collision detection
      const collisionErrors = deepMerge(merged, fileConfig, `config/${filename}`)
      if (collisionErrors.length > 0) {
        legacyErrors.push(...collisionErrors)
        continue
      }

      result.files.push(`config/${filename}`)
    } catch (err) {
      legacyErrors.push(`config/${filename}: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // Resolve legacy-load failures. .rundot/ is canonical and wins, so a broken legacy layout must
  // not discard a valid .rundot/ config — warn loudly and proceed with .rundot/ only. Without a
  // .rundot/ config present, the legacy errors are fatal, as before.
  if (legacyErrors.length > 0) {
    if (rundotConfig) {
      // Keep the legacy config that DID load; only the broken files are skipped. .rundot/ overlays
      // and wins below, so valid legacy-only keys still apply.
      result.warnings.push(
        `Skipped invalid legacy config (using ${rundotConfig.prefix} where it overlaps) — fix or migrate the ` +
        'legacy files (`rundot migrate-config`): ' + legacyErrors.join('; '),
      )
      stripUnwrappedSimulationKeys(merged)
    } else {
      result.errors.push(...legacyErrors)
      return result
    }
  }

  // No config files found at all (legacy or .rundot/)
  if (result.files.length === 0 && !rundotConfig) {
    return result
  }

  // If there were merge errors, don't return config
  if (result.errors.length > 0) {
    return result
  }

  // Overlay .rundot/ on top of the legacy result so it always wins. Done before the
  // structure/nested-array/cross-reference validations so they run on the final merged output.
  if (rundotConfig) {
    const prefix = rundotConfig.prefix
    const conflicts = Object.keys(rundotConfig.config).filter(k => k in merged)
    if (conflicts.length > 0) {
      result.warnings.push(
        `Config found in both ${prefix} and a legacy location. Using ${prefix} for: ${conflicts.join(', ')}`,
      )
      // Env-specific base overrides are silently dropped when the rundot folder wins — call it out.
      const shadowed = conflicts.filter(k => envBaseKeys.has(k))
      if (shadowed.length > 0 && baseFilename) {
        result.warnings.push(
          `${baseFilename} defines per-environment overrides for [${shadowed.join(', ')}], but ` +
          `${prefix} takes priority and these are ignored. ${prefix} is env-agnostic — move env-specific ` +
          `tweaks into ${prefix} or remove them from ${baseFilename}.`,
        )
      }
      for (const key of conflicts) {
        delete (merged as Record<string, unknown>)[key]
      }
    }
    // rundot keys are whole-system values; conflicts already removed, so a shallow
    // top-level overlay is correct (and avoids deepMerge's throw-on-conflict).
    Object.assign(merged, rundotConfig.config)
    result.files.push(...rundotConfig.files)
  }

  // Migration nudge: if legacy files carry server config but .rundot/ is absent, hint at migration.
  const SERVER_CONFIG_KEYS = ['simulation', 'leaderboard', 'rooms', 'shop', 'ugc', 'storage', 'inbox']
  if (!rundotConfig && result.files.length > 0 && SERVER_CONFIG_KEYS.some(k => k in merged)) {
    result.warnings.push(
      'Server config uses the legacy layout (still fully supported). ' +
      'Run `rundot migrate-config` to move it into rundot/ (use --dry-run first to preview).',
    )
  }

  // Step 4: Validate structure consistency on the merged result
  if (hasSimulationContent(merged)) {
    result.errors.push(
      `Merged config has simulation data (entities, recipes, lifecycle) at the top level. ` +
      `Move simulation files into rundot/simulation/ (run \`rundot migrate-config --dry-run\`) ` +
      `or wrap them: {"entities": ...} → {"simulation": {"entities": ...}}.`,
    )
    return result
  }

  // Step 5: Validate no nested arrays (Firestore limitation)
  const nestedArrayErrors = findNestedArrays(merged, '')
  if (nestedArrayErrors.length > 0) {
    result.errors.push(...nestedArrayErrors.map(path =>
      `Nested array at "${path}": Firestore does not support arrays containing arrays`,
    ))
    return result
  }

  // Step 6: Cross-reference validation (warnings only)
  validateCrossReferences(merged, result.warnings)

  // Return empty config as null (nothing useful to upload)
  if (Object.keys(merged).length === 0) {
    return result
  }

  result.config = merged
  return result
}

/**
 * Check if an object contains simulation-related keys at the top level.
 */
function hasSimulationContent(obj: Record<string, unknown>): boolean {
  return 'entities' in obj || 'recipes' in obj || 'lifecycle' in obj
}

/**
 * Check if an object has simulation keys WITHOUT a simulation wrapper.
 * Used for per-file validation where a file may legitimately use e.g.
 * "lifecycle" as top-level metadata alongside a proper "simulation" block.
 */
function hasUnwrappedSimulationContent(obj: Record<string, unknown>): boolean {
  return hasSimulationContent(obj) && !('simulation' in obj)
}

const SIMULATION_CONTENT_KEYS = ['entities', 'recipes', 'lifecycle'] as const

function stripUnwrappedSimulationKeys(obj: Record<string, unknown>): void {
  for (const key of SIMULATION_CONTENT_KEYS) {
    delete obj[key]
  }
}

/**
 * Deep merge source into target with collision detection.
 * Returns an array of error messages for conflicting leaf values.
 */
function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
  sourceFile: string,
  path = '',
): string[] {
  const errors: string[] = []

  for (const key of Object.keys(source)) {
    const fullPath = path ? `${path}.${key}` : key
    const targetVal = target[key]
    const sourceVal = source[key]

    if (targetVal === undefined) {
      target[key] = sourceVal
    } else if (
      isNonEmptyObject(targetVal) && isNonEmptyObject(sourceVal)
    ) {
      // Both are NON-EMPTY objects — recurse. (Matches the CLI's CheckForCollisions /
      // RundotConfigResolver: an empty object vs a non-empty object is a leaf collision, not a
      // merge, so playground and deploy agree.)
      errors.push(...deepMerge(
        targetVal as Record<string, unknown>,
        sourceVal as Record<string, unknown>,
        sourceFile,
        fullPath,
      ))
    } else if (JSON.stringify(targetVal) === JSON.stringify(sourceVal)) {
      // Identical duplicates are allowed
    } else {
      errors.push(`${sourceFile}: Conflicting value at "${fullPath}" — cannot merge different leaf values`)
    }
  }

  return errors
}

/**
 * Find nested arrays (arrays containing arrays) which Firestore doesn't support.
 */
function findNestedArrays(obj: unknown, path: string): string[] {
  const errors: string[] = []

  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i++) {
      const item = obj[i]
      if (Array.isArray(item)) {
        errors.push(path ? `${path}[${i}]` : `[${i}]`)
      } else if (isPlainObject(item)) {
        errors.push(...findNestedArrays(item, path ? `${path}[${i}]` : `[${i}]`))
      }
    }
  } else if (isPlainObject(obj)) {
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const fullPath = path ? `${path}.${key}` : key
      errors.push(...findNestedArrays(value, fullPath))
    }
  }

  return errors
}

/**
 * Validate cross-references between entities, recipes, and loot tables.
 * Adds warnings (not errors) for missing references.
 */
function validateCrossReferences(config: Record<string, unknown>, warnings: string[]): void {
  const sim = config.simulation as Record<string, unknown> | undefined
  if (!sim) return

  const entities = sim.entities as Record<string, unknown> | undefined
  const recipes = sim.recipes as Record<string, unknown> | undefined

  if (!entities || !recipes) return

  const entityIds = new Set(Object.keys(entities))

  // Check recipe references to entities
  for (const [recipeId, recipeDef] of Object.entries(recipes)) {
    if (!isPlainObject(recipeDef)) continue
    const recipe = recipeDef as Record<string, unknown>

    // Check endEffects for entity references
    const endEffects = recipe.endEffects as Array<Record<string, unknown>> | undefined
    if (Array.isArray(endEffects)) {
      for (const effect of endEffects) {
        if (isPlainObject(effect) && typeof effect.entity === 'string') {
          if (!isTemplateRef(effect.entity) && !entityIds.has(effect.entity)) {
            warnings.push(
              `Recipe "${recipeId}" references entity "${effect.entity}" which is not defined in entities`,
            )
          }
        }
      }
    }

    // Check requirements for entity references
    const requirements = recipe.requirements as Array<Record<string, unknown>> | undefined
    if (Array.isArray(requirements)) {
      for (const req of requirements) {
        if (isPlainObject(req) && typeof req.entity === 'string') {
          if (!isTemplateRef(req.entity) && !entityIds.has(req.entity)) {
            warnings.push(
              `Recipe "${recipeId}" requirement references entity "${req.entity}" which is not defined`,
            )
          }
        }
      }
    }
  }
}

/**
 * Check if a reference contains template placeholders (e.g. `{{entity}}_stage`).
 * These are resolved at runtime and can't be validated statically.
 */
function isTemplateRef(ref: string): boolean {
  return ref.includes('{{') && ref.includes('}}')
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * A realtime.config.json room is "deterministic" when its `deterministic` field is
 * present and not `false` — i.e. `true` OR the options-object form
 * ({protocol, simulationHooks}). Only these belong in the uploaded `value.rooms`;
 * a non-deterministic (`file`) room needs a deployed creator bundle instead.
 */
function isDeterministicRoom(room: unknown): room is Record<string, unknown> {
  return isPlainObject(room) && room.deterministic !== undefined && room.deterministic !== false
}

/** Room-level fields a realtime.config.json entry may declare (see `RoomTypeDefinition`). */
const ROOM_DEF_KEYS = new Set(['type', 'file', 'export', 'singleton', 'persistent', 'config', 'deterministic'])

/**
 * Per-room tunables that live under `config`, not at the room's top level. Writing
 * one a level up is the natural mistake and every consumer silently ignores it —
 * the room then seats ROOM_DEFAULT_MAX_PLAYERS (10) instead of the requested
 * capacity — so name the exact fix rather than dropping the key.
 */
const ROOM_CONFIG_KEYS = new Set([
  'maxPlayers', 'idleTimeout', 'autoPersist', 'persistInterval', 'allowReconnect',
  'reconnectTimeout', 'startLocked', 'tickInterval', 'tickRateHz', 'waitForFullRoom', 'metadata',
])

/** Plausible-looking fields that have no equivalent anywhere, and why. */
const UNSUPPORTED_ROOM_KEYS: Record<string, string> = {
  minPlayers: 'a Syncplay match starts as soon as the first player joins; unfilled slots take the canonical null input, so there is no minimum to configure',
}

function assertRealtimeRoomShape(room: Record<string, unknown>, index: number, displayName: string): void {
  const label = typeof room.type === 'string' ? `"${room.type}"` : `#${index}`
  for (const key of Object.keys(room)) {
    if (ROOM_DEF_KEYS.has(key)) continue
    if (ROOM_CONFIG_KEYS.has(key)) {
      throw new Error(
        `${displayName}: room ${label} sets "${key}" at the top level, where it is ignored. `
        + `Move it under "config": { "type": ..., "config": { "${key}": ${JSON.stringify(room[key])} } }`,
      )
    }
    const reason = UNSUPPORTED_ROOM_KEYS[key]
    throw new Error(
      `${displayName}: room ${label} has unknown field "${key}"`
      + (reason ? ` — ${reason}` : `. Supported fields: ${[...ROOM_DEF_KEYS].join(', ')}`),
    )
  }
}

/**
 * The deterministic rooms declared in a realtime.config.json (`{"rooms": [...]}`).
 * Throws (loudly, naming the file) on malformed JSON via `parseJsonFile`, and on a
 * room field that would otherwise be silently dropped.
 */
function readDeterministicRealtimeRooms(realtimePath: string, displayName: string): Record<string, unknown>[] {
  const parsed = parseJsonFile(realtimePath, displayName)
  const roomList = isPlainObject(parsed) && Array.isArray((parsed as { rooms?: unknown }).rooms)
    ? (parsed as { rooms: unknown[] }).rooms
    : []
  roomList.forEach((room, index) => {
    if (isPlainObject(room)) assertRealtimeRoomShape(room, index, displayName)
  })
  return roomList.filter(isDeterministicRoom)
}

function isNonEmptyObject(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value) && Object.keys(value).length > 0
}
